// Shared phone model list for the PnP assign dialog and User editor Add Phone modal.
// Covers every vendor that has a provisioning handler in api/lib/provision/.
// value = model string sent to the backend (stored as device_model in v_devices and
//         used by the provision router); label = human-readable display.
// Grandstream internal shortcodes (gs-1628 etc.) are NOT used here -- the backend
// normalises them to the real model name when generating config, but canonical names
// (GXP1628, GRP2615) are cleaner for new assignments.

export interface PhoneModel {
  value: string;
  label: string;
}

export interface PhoneModelGroup {
  /** Vendor key matching stratus_discovered_phones.vendor_key */
  vendorKey: string;
  /** Group heading shown in the <optgroup> */
  group: string;
  models: PhoneModel[];
}

export const PHONE_MODEL_GROUPS: PhoneModelGroup[] = [
  {
    vendorKey: 'grandstream',
    group: 'Grandstream GRP series (modern)',
    models: [
      { value: 'GRP2601', label: 'GRP2601' },
      { value: 'GRP2602', label: 'GRP2602' },
      { value: 'GRP2603', label: 'GRP2603' },
      { value: 'GRP2604', label: 'GRP2604' },
      { value: 'GRP2612', label: 'GRP2612' },
      { value: 'GRP2613', label: 'GRP2613' },
      { value: 'GRP2614', label: 'GRP2614' },
      { value: 'GRP2615', label: 'GRP2615' },
      { value: 'GRP2616', label: 'GRP2616' },
      { value: 'GRP2624', label: 'GRP2624' },
      { value: 'GRP2634', label: 'GRP2634' },
    ],
  },
  {
    vendorKey: 'grandstream',
    group: 'Grandstream GXP series',
    models: [
      { value: 'GXP1615', label: 'GXP1615' },
      { value: 'GXP1625', label: 'GXP1625' },
      { value: 'GXP1628', label: 'GXP1628' },
      { value: 'GXP1630', label: 'GXP1630' },
      { value: 'GXP1760', label: 'GXP1760' },
      { value: 'GXP1780', label: 'GXP1780' },
      { value: 'GXP2130', label: 'GXP2130' },
      { value: 'GXP2135', label: 'GXP2135' },
      { value: 'GXP2140', label: 'GXP2140' },
      { value: 'GXP2160', label: 'GXP2160' },
      { value: 'GXP2170', label: 'GXP2170' },
    ],
  },
  {
    vendorKey: 'grandstream',
    group: 'Grandstream ATA / Cordless',
    models: [
      { value: 'HT801', label: 'HT801 (1-port ATA)' },
      { value: 'HT802', label: 'HT802 (2-port ATA)' },
      { value: 'HT812', label: 'HT812 (2-port ATA)' },
      { value: 'HT814', label: 'HT814 (4-port ATA)' },
      { value: 'WP820', label: 'WP820 (Wi-Fi cordless)' },
      { value: 'WP822', label: 'WP822 (Wi-Fi cordless)' },
      { value: 'WP825', label: 'WP825 (Wi-Fi cordless)' },
      { value: 'DP720', label: 'DP720 (DECT handset)' },
      { value: 'DP750', label: 'DP750 (DECT base)' },
      { value: 'DP752', label: 'DP752 (DECT base)' },
      { value: 'DP755', label: 'DP755 (DECT base)' },
    ],
  },
  {
    vendorKey: 'yealink',
    group: 'Yealink T4 series',
    models: [
      { value: 'T41S', label: 'T41S' },
      { value: 'T42S', label: 'T42S' },
      { value: 'T43U', label: 'T43U' },
      { value: 'T46S', label: 'T46S' },
      { value: 'T46U', label: 'T46U' },
      { value: 'T48S', label: 'T48S' },
      { value: 'T48U', label: 'T48U' },
      { value: 'T53', label: 'T53' },
      { value: 'T54W', label: 'T54W' },
      { value: 'T57W', label: 'T57W' },
      { value: 'T58A', label: 'T58A' },
    ],
  },
  {
    vendorKey: 'yealink',
    group: 'Yealink DECT / Conference',
    models: [
      { value: 'W52P', label: 'W52P (DECT base)' },
      { value: 'W56H', label: 'W56H (DECT handset)' },
      { value: 'W70B', label: 'W70B (DECT base)' },
      { value: 'CP860', label: 'CP860 (conference)' },
      { value: 'CP920', label: 'CP920 (conference)' },
    ],
  },
  {
    vendorKey: 'cisco',
    group: 'Cisco IP Phone',
    models: [
      { value: 'CP6821', label: 'CP6821' },
      { value: 'CP6851', label: 'CP6851' },
      { value: 'CP6861', label: 'CP6861' },
      { value: 'CP6871', label: 'CP6871' },
      { value: 'CP7811', label: 'CP7811' },
      { value: 'CP7821', label: 'CP7821' },
      { value: 'CP7841', label: 'CP7841' },
      { value: 'CP7861', label: 'CP7861' },
      { value: 'CP8811', label: 'CP8811' },
      { value: 'CP8841', label: 'CP8841' },
      { value: 'CP8851', label: 'CP8851' },
      { value: 'CP8861', label: 'CP8861' },
      { value: 'CP8865', label: 'CP8865' },
    ],
  },
  {
    vendorKey: 'polycom',
    group: 'Polycom VVX',
    models: [
      { value: 'VVX201', label: 'VVX201' },
      { value: 'VVX300', label: 'VVX300' },
      { value: 'VVX301', label: 'VVX301' },
      { value: 'VVX310', label: 'VVX310' },
      { value: 'VVX311', label: 'VVX311' },
      { value: 'VVX400', label: 'VVX400' },
      { value: 'VVX401', label: 'VVX401' },
      { value: 'VVX410', label: 'VVX410' },
      { value: 'VVX411', label: 'VVX411' },
      { value: 'VVX450', label: 'VVX450' },
      { value: 'VVX500', label: 'VVX500' },
      { value: 'VVX600', label: 'VVX600' },
    ],
  },
  {
    vendorKey: 'snom',
    group: 'Snom',
    models: [
      { value: 'D305', label: 'D305' },
      { value: 'D315', label: 'D315' },
      { value: 'D335', label: 'D335' },
      { value: 'D345', label: 'D345' },
      { value: 'D375', label: 'D375' },
      { value: 'D385', label: 'D385' },
      { value: 'D712', label: 'D712' },
      { value: 'D715', label: 'D715' },
      { value: 'D717', label: 'D717' },
      { value: 'D735', label: 'D735' },
      { value: 'D745', label: 'D745' },
      { value: 'D765', label: 'D765' },
      { value: 'D785', label: 'D785' },
    ],
  },
  {
    vendorKey: 'fanvil',
    group: 'Fanvil',
    models: [
      { value: 'X3U', label: 'X3U' },
      { value: 'X4U', label: 'X4U' },
      { value: 'X5U', label: 'X5U' },
      { value: 'X6U', label: 'X6U' },
      { value: 'X7A', label: 'X7A' },
      { value: 'X210', label: 'X210' },
      { value: 'X301G', label: 'X301G' },
    ],
  },
  {
    vendorKey: 'audiocodes',
    group: 'AudioCodes',
    models: [
      { value: '405HD', label: '405HD' },
      { value: '405', label: '405' },
      { value: '410HD', label: '410HD' },
      { value: '440HD', label: '440HD' },
      { value: '445HD', label: '445HD' },
    ],
  },
  {
    vendorKey: 'mitel',
    group: 'Mitel',
    models: [
      { value: '6863i', label: '6863i' },
      { value: '6865i', label: '6865i' },
      { value: '6867i', label: '6867i' },
      { value: '6869i', label: '6869i' },
      { value: '6873i', label: '6873i' },
    ],
  },
  {
    vendorKey: 'sangoma',
    group: 'Sangoma',
    models: [
      { value: 'S205', label: 'S205' },
      { value: 'S300', label: 'S300' },
      { value: 'S400', label: 'S400' },
      { value: 'S500', label: 'S500' },
      { value: 'S700', label: 'S700' },
      { value: 'S705', label: 'S705' },
    ],
  },
  {
    vendorKey: 'gigaset',
    group: 'Gigaset',
    models: [
      { value: 'Maxwell2', label: 'Maxwell 2' },
      { value: 'Maxwell3', label: 'Maxwell 3' },
      { value: 'Maxwell4', label: 'Maxwell 4' },
      { value: 'Maxwell10', label: 'Maxwell 10' },
      { value: 'DE310IPPro', label: 'DE310 IP Pro' },
      { value: 'DE410IPPro', label: 'DE410 IP Pro' },
    ],
  },
  {
    vendorKey: 'panasonic',
    group: 'Panasonic',
    models: [
      { value: 'KX-HDV130', label: 'KX-HDV130' },
      { value: 'KX-HDV230', label: 'KX-HDV230' },
      { value: 'KX-HDV330', label: 'KX-HDV330' },
      { value: 'KX-HDV430', label: 'KX-HDV430' },
    ],
  },
  {
    vendorKey: 'htek',
    group: 'Htek',
    models: [
      { value: 'UC902', label: 'UC902' },
      { value: 'UC903', label: 'UC903' },
      { value: 'UC912', label: 'UC912' },
      { value: 'UC923', label: 'UC923' },
      { value: 'UC924', label: 'UC924' },
      { value: 'UC926', label: 'UC926' },
    ],
  },
];

/**
 * Flat PHONE_MODELS array (groups collapsed), compatible with the legacy
 * UserEditor.tsx shape { group: string; models: string[] }.
 * Kept for backward-compat with the ModelSelect component.
 */
export const PHONE_MODELS: { group: string; models: string[] }[] = PHONE_MODEL_GROUPS.map(g => ({
  group: g.group,
  models: g.models.map(m => m.value),
}));

/**
 * Return a copy of PHONE_MODEL_GROUPS with the given vendorKey's groups
 * moved to the front. Used by the PnP assign dialog to surface the
 * discovered phone's vendor at the top of the picker.
 */
export function prioritiseVendor(vendorKey: string | null | undefined): PhoneModelGroup[] {
  if (!vendorKey) return PHONE_MODEL_GROUPS;
  const key = vendorKey.toLowerCase();
  const matched = PHONE_MODEL_GROUPS.filter(g => g.vendorKey === key);
  const rest    = PHONE_MODEL_GROUPS.filter(g => g.vendorKey !== key);
  return [...matched, ...rest];
}
